import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Card } from '@/components/ui/Card';

interface UserPreferences {
  theme?: 'light' | 'dark' | 'system';
  language?: 'nb' | 'en';
  notifications?: {
    email?: boolean;
    browser?: boolean;
  };
}

interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  preferences: UserPreferences;
}

async function fetchProfile(): Promise<UserProfile> {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/users/me`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) throw new Error('Failed to fetch profile');
  return response.json();
}

async function updatePreferences(data: UserPreferences) {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/users/me/preferences`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to update preferences');
  return response.json();
}

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('system');
  const [language, setLanguage] = useState<'nb' | 'en'>('nb');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [browserNotifications, setBrowserNotifications] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch user profile for preferences
  const { data: profile } = useQuery<UserProfile>({
    queryKey: ['profile'],
    queryFn: fetchProfile,
  });

  // Initialize state from profile
  useEffect(() => {
    if (profile?.preferences) {
      setTheme(profile.preferences.theme || 'system');
      setLanguage(profile.preferences.language || 'nb');
      setEmailNotifications(profile.preferences.notifications?.email ?? true);
      setBrowserNotifications(profile.preferences.notifications?.browser ?? false);
    }
  }, [profile]);

  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: updatePreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setHasChanges(false);
    },
  });

  const handleSave = () => {
    updatePreferencesMutation.mutate({
      theme,
      language,
      notifications: {
        email: emailNotifications,
        browser: browserNotifications,
      },
    });
  };

  return (
    <div className="container mx-auto max-w-3xl py-8">
      <h1 className="mb-8 text-3xl font-bold">Innstillinger</h1>

      {/* Theme Settings */}
      <Card className="mb-6">
        <div className="p-6">
          <h2 className="mb-4 text-xl font-semibold">Tema</h2>
          <p className="mb-4 text-sm text-gray-600">
            Velg hvordan applikasjonen skal vises
          </p>

          <div className="flex gap-2">
            <Button
              variant={theme === 'light' ? 'default' : 'outline'}
              onClick={() => {
                setTheme('light');
                setHasChanges(true);
              }}
            >
              ☀️ Lys
            </Button>
            <Button
              variant={theme === 'dark' ? 'default' : 'outline'}
              onClick={() => {
                setTheme('dark');
                setHasChanges(true);
              }}
            >
              🌙 Mørk
            </Button>
            <Button
              variant={theme === 'system' ? 'default' : 'outline'}
              onClick={() => {
                setTheme('system');
                setHasChanges(true);
              }}
            >
              💻 System
            </Button>
          </div>
        </div>
      </Card>

      {/* Language Settings */}
      <Card className="mb-6">
        <div className="p-6">
          <h2 className="mb-4 text-xl font-semibold">Språk</h2>
          <p className="mb-4 text-sm text-gray-600">
            Velg språk for brukergrensesnittet
          </p>

          <div className="flex gap-2">
            <Button
              variant={language === 'nb' ? 'default' : 'outline'}
              onClick={() => {
                setLanguage('nb');
                setHasChanges(true);
              }}
            >
              🇳🇴 Norsk
            </Button>
            <Button
              variant={language === 'en' ? 'default' : 'outline'}
              onClick={() => {
                setLanguage('en');
                setHasChanges(true);
              }}
            >
              🇬🇧 English
            </Button>
          </div>
        </div>
      </Card>

      {/* Notification Settings */}
      <Card className="mb-6">
        <div className="p-6">
          <h2 className="mb-4 text-xl font-semibold">Varsler</h2>
          <p className="mb-4 text-sm text-gray-600">
            Administrer hvordan du vil motta varsler
          </p>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="emailNotifications">E-postvarsler</Label>
                <p className="text-sm text-gray-600">
                  Motta varsler på e-post
                </p>
              </div>
              <button
                id="emailNotifications"
                role="switch"
                aria-checked={emailNotifications}
                onClick={() => {
                  setEmailNotifications(!emailNotifications);
                  setHasChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  emailNotifications ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    emailNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="browserNotifications">Nettlesermeldinger</Label>
                <p className="text-sm text-gray-600">
                  Motta varsler i nettleseren
                </p>
              </div>
              <button
                id="browserNotifications"
                role="switch"
                aria-checked={browserNotifications}
                onClick={() => {
                  setBrowserNotifications(!browserNotifications);
                  setHasChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  browserNotifications ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    browserNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Save Button */}
      {hasChanges && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={updatePreferencesMutation.isPending}
            size="lg"
          >
            {updatePreferencesMutation.isPending ? 'Lagrer...' : 'Lagre endringer'}
          </Button>
        </div>
      )}
    </div>
  );
}
